import Divider from '@/components/common/Divider';
import ExportCSV from '@/components/common/ExportCSV';
import Tooltip from '@/components/common/Tooltip';
import { BlotterItem } from '@/model/blotters';
import { findOrderHistory } from '@/services/OrderService';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectCredentials, User } from '@/state/reducers/authSlice';
import { setHistoricTrades } from '@/state/reducers/blotterSlice';
import useScreenSizes from '@/utils/hooks/useScreenSizes';
import { copyStyles } from '@/utils/popout';
import cn from 'classnames';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { isTablet } from 'react-device-detect';
import { createPortal } from 'react-dom';
import { MdOpenInNew } from 'react-icons/md';
import useSWR from 'swr';
import Loader from '../common/Loader';
import PopOut, { PopOutHeader, PopOutTitle } from '../common/PopOut';
import OrderHistoryTable from '../modules/Blotters/OrderHistoryTable';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from './Modal';

interface DealHistoryModalProps extends ModalProps {
    order: BlotterItem;
    linkedOrder?: BlotterItem;
}

const DealHistoryModal = (props: DealHistoryModalProps) => {
    const { opened, handlers } = props;
    const dispatch = useAppDispatch();

    const screen = useScreenSizes();
    const [CSVData, setCSVData] = useState<any[]>([]);
    const [popOut, setPopOut] = useState<Window | null>(null);

    useEffect(() => {
        dispatch(setHistoricTrades(null));
        if (opened && popOut) {
            popOut.close();
            setPopOut(null);
        }
    }, [opened]);

    const openPopOutWindow = () => {
        const popOutWindow = window.open('', '', 'width=1200,height=600');
        if (popOutWindow) {
            copyStyles(document, popOutWindow.document);
            setPopOut(popOutWindow);
            handlers.close();
        }
    };

    return (
        <Fragment>
            <Modal {...props} size="sm:max-w-[90vw] h-full lg:h-[600px]">
                <ModalHeader>
                    <div className="flex space-x-2 items-center">
                        <ModalTitle>Order History</ModalTitle>
                        <ExportCSV className="w-5 h-5" csvData={CSVData} />
                        {(screen.laptop || screen.desktop) && !isTablet && (
                            <Tooltip
                                width={120}
                                content="Open in Separate Window"
                                containerClassName="h-5"
                                placement="top-start">
                                <button type="button" className="text-xl" onClick={openPopOutWindow}>
                                    <MdOpenInNew />
                                </button>
                            </Tooltip>
                        )}
                    </div>
                    <ModalClose handlers={handlers} />
                </ModalHeader>
                <DealHistoryBody setCSVData={setCSVData} {...props} />
            </Modal>
            {popOut &&
                createPortal(
                    <PopOut>
                        <PopOutHeader>
                            <PopOutTitle>Order History</PopOutTitle>
                            <Tooltip content="Download to CSV" className="-bottom-10 w-max">
                                <ExportCSV className="w-5 h-5" csvData={CSVData} />
                            </Tooltip>
                        </PopOutHeader>
                        <DealHistoryBody setCSVData={setCSVData} {...props} />
                    </PopOut>,
                    popOut.document.body
                )}
        </Fragment>
    );
};

export default DealHistoryModal;

interface DealHistoryBodyProps extends DealHistoryModalProps {
    setCSVData: (data: any[]) => void;
}

const DealHistoryBody = (props: DealHistoryBodyProps) => {
    const { order, linkedOrder, setCSVData } = props;
    const credentials = useAppSelector(selectCredentials);

    const { data, isLoading } = useSWR(
        'orderHistory' + order.orderId,
        () => fetchOrderHistory(credentials, order.orderId),
        { refreshInterval: 5000 }
    );

    const { data: linkedData, isLoading: isLoadingLinkedData } = useSWR(
        'orderHistory' + linkedOrder?.orderId,
        () => fetchOrderHistory(credentials, linkedOrder?.orderId),
        { refreshInterval: 5000 }
    );

    const [orderHistory, setOrderHistory] = useState('Current');
    const parentStrategyID = useMemo(() => {
        if (linkedOrder) {
            return linkedOrder.metadata.find((data) => data.key === 'PARENT_STRATEGY_ID')?.value;
        }
        return null;
    }, [linkedOrder]);

    return (
        <div className="flex flex-col w-full h-full">
            <Divider />
            {linkedOrder && (
                <div className="flex p-2">
                    <button
                        className={cn('text-sm py-1 px-2 text-brand-text border', {
                            'border-brand-primary bg-brand-primary': orderHistory === 'Current',
                            'border-brand-neutral-600 hover:bg-brand-primary-light hover:border-brand-primary-light':
                                orderHistory !== 'Current',
                            'border-r-0': true
                        })}
                        onClick={() => setOrderHistory('Current')}>
                        Current Order
                    </button>
                    <button
                        className={cn('text-sm py-1 px-2 text-brand-text border', {
                            'border-brand-primary bg-brand-primary': orderHistory === 'Linked',
                            'border-brand-neutral-600 hover:bg-brand-primary-light hover:border-brand-primary-light':
                                orderHistory !== 'Linked'
                        })}
                        onClick={() => setOrderHistory('Linked')}>
                        Linked Order
                    </button>
                </div>
            )}
            {parentStrategyID && <div className="text-brand-text text-sm px-2">{parentStrategyID}</div>}

            {(isLoading || isLoadingLinkedData) && (
                <div className="flex justify-center items-center flex-1">
                    <Loader className="text-brand-primary" />
                </div>
            )}

            {!(isLoading || isLoadingLinkedData) && !!data?.length && (
                <div className="flex-1 basis-0 p-2 overflow-hidden">
                    <OrderHistoryTable
                        orderHistories={orderHistory === 'Current' ? data || [] : linkedData || []}
                        setCSVData={setCSVData}
                    />
                </div>
            )}

            {!(isLoading || isLoadingLinkedData) && !data?.length && (
                <div className="historic-search-empty p-2 flex justify-center items-center text-center flex-1 text-brand-neutral-400 text-sm font-bold italic">
                    {/* TBA - Translations */}
                    <div>No History Was Found For This Order.</div>
                </div>
            )}
        </div>
    );
};

async function fetchOrderHistory(credentials?: User, orderId?: string) {
    if (credentials && orderId) return await findOrderHistory({ orderId }, credentials);
    return [];
}
